import React, { useState, useEffect } from "react";

import { PageHero, Pagination, ViewToggle, type ViewMode } from "../components/common";
import BlogCard from "../components/blog/BlogCard";
import { Newsletter } from "../components/sections";
import { useBlogPosts } from "../hooks/useBlogPosts";
import type { BlogPost } from "../types";
import { formatDisplayDate } from "../utils/formatDisplayDate";

const POSTS_PER_PAGE = 6;
const FALLBACK_IMAGE = "/images/initiatives/Picture1.png";
const VIEW_STORAGE_KEY = "blog-view";

const Blog: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  useEffect(() => {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY) as ViewMode | null;
    if (stored === "grid" || stored === "list") setViewMode(stored);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_STORAGE_KEY, mode);
  };
  const {
    posts: apiPosts,
    totalCount,
    loading,
  } = useBlogPosts(currentPage, POSTS_PER_PAGE);

  const mappedApiPosts: BlogPost[] = apiPosts.map((post) => ({
    slug: post.id ? String(post.id) : undefined,
    image: post.eng_thumbnail || FALLBACK_IMAGE,
    imageAlt: `${post.eng_title || "Blog"} Image`,
    title: post.eng_title || "Blog post",
    excerpt: post.eng_caption || "",
    date: formatDisplayDate(
      // Prefer created_at when available, then created_date, then published_at
      (post as any).created_at || post.created_date || post.published_at || ""
    ),
    readTime: "",
    tags: Array.isArray(post.eng_tags)
      ? post.eng_tags
          .map((tag) => tag?.tag_name_eng)
          .filter((tagName): tagName is string => Boolean(tagName))
      : [],
    link: "#",
  }));

  const totalPages = Math.max(
    1,
    Math.ceil((totalCount || mappedApiPosts.length) / POSTS_PER_PAGE)
  );

  return (
    <main id="main-content" role="main">
      <PageHero
        title="AI Literacy Blog"
        subtitle="Discover insights, tutorials, and resources to enhance your AI education journey"
        className="blog-hero"
      />

      <section
        className="blog-page-section"
        style={{ padding: "80px 0 100px 0", background: "white" }}
      >
        <div className="container">
          <div
            className="d-flex justify-content-end align-items-center mb-4"
            style={{ gap: "0.5rem" }}
          >
            <span className="text-muted" style={{ fontSize: "0.9rem", marginRight: "0.5rem" }}>
              View
            </span>
            <ViewToggle
              value={viewMode}
              onChange={handleViewChange}
              ariaLabel="Blog view: grid or list"
            />
          </div>
          <div className="row">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading blogs...</span>
                </div>
              </div>
            ) : (
              mappedApiPosts?.map((post, index) => (
                <BlogCard key={post.slug ?? index} post={post} variant={viewMode} />
              ))
            )}
          </div>

          {!loading && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </section>

      <Newsletter />
    </main>
  );
};

export default Blog;
